import { Injectable } from "@angular/core";
import * as Sentry from "@sentry/angular";
import { Span } from "@sentry/angular";

@Injectable({
    providedIn: "root",
})
export class SentryService {
    /**
     * Tag para identificar quando um erro deve ser ignorado pelo interceptor
     */
    static readonly IGNORE_INTERCEPTOR_TAG = "_sentry_ignore_interceptor";
    /**
     * Configura o usuário logado no Sentry
     */
    setUser(user: {
        id: string;
        email?: string;
        username?: string;
        role?: string;
        [key: string]: any;
    }): void {
        Sentry.setUser(user);

        // Adicionar breadcrumb de login
        this.addBreadcrumb(
            `Usuário ${user.username || user.email} logou no sistema`,
            "auth",
            { userId: user.id, role: user.role }
        );
    }

    /**
     * Remove dados do usuário (logout)
     */
    clearUser(): void {
        this.addBreadcrumb("Usuário fez logout", "auth");
        Sentry.setUser(null);
    }

    /**
     * Adiciona contexto de módulo/seção
     */
    setModuleContext(module: string, section?: string): void {
        Sentry.setTag("module", module);
        if (section) {
            Sentry.setTag("section", section);
        }

        // Adicionar contexto adicional baseado no módulo
        Sentry.setContext("navigation", {
            module,
            section,
            timestamp: new Date().toISOString(),
        });

        this.addBreadcrumb(
            `Navegou para ${module}${section ? ` - ${section}` : ""}`,
            "navigation",
            { module, section }
        );
    }

    /**
     * Captura erro personalizado com contexto adicional
     */
    captureError(error: Error, context?: Record<string, any>): void {
        Sentry.withScope((scope) => {
            if (context) {
                // Adicionar contexto como extras
                Object.entries(context).forEach(([key, value]) => {
                    scope.setExtra(key, value);
                });

                // Se há informações de negócio, adicionar como tags também
                if (context["operation"])
                    scope.setTag("operation", context["operation"]);
                if (context["component"])
                    scope.setTag("component", context["component"]);
                if (context["api_endpoint"])
                    scope.setTag("api_endpoint", context["api_endpoint"]);
            }

            Sentry.captureException(error);
        });
    }

    /**
     * Adiciona breadcrumb personalizado para rastreamento de ações
     */
    addBreadcrumb(
        message: string,
        category: string,
        data?: any,
        level: "info" | "warning" | "error" = "info"
    ): void {
        Sentry.addBreadcrumb({
            message,
            category,
            data,
            level,
            timestamp: Date.now() / 1000,
        });
    }

    /**
     * Captura mensagem de log personalizada
     */
    captureMessage(
        message: string,
        level: "info" | "warning" | "error" = "info",
        extra?: Record<string, any>
    ): void {
        Sentry.withScope((scope) => {
            if (extra) {
                Object.entries(extra).forEach(([key, value]) => {
                    scope.setExtra(key, value);
                });
            }
            Sentry.captureMessage(message, level);
        });
    }

    /**
     * Rastreia performance de operações críticas
     */
    startSpan(
        name: string,
        operation: string = "custom",
        callback: (span: Span) => void
    ): any {
        return Sentry.startSpan(
            {
                name,
                op: operation,
            },
            callback
        );
    }

    /**
     * Adiciona fingerprint personalizado para agrupar erros similares
     */
    setFingerprint(fingerprint: string[]): void {
        Sentry.withScope((scope) => {
            scope.setFingerprint(fingerprint);
        });
    }

    /**
     * Define uma tag no contexto atual do Sentry
     */
    setTag(key: string, value: string): void {
        Sentry.setTag(key, value);
    }
}
